import type { WizardStepKey } from '@bk/contracts';
import type { ReactNode } from 'react';

/**
 * Illustrazioni dei passi della configurazione guidata (ADR 0022): disegni vettoriali leggeri nei colori del tema,
 * senza servizi esterni. Una scena per passo, con lo stesso stile.
 */
const A = 'var(--color-accent)';
const INK = 'var(--color-ink)';

function Frame({ children, label }: { children: ReactNode; label: string }) {
  return (
    <svg
      viewBox="0 0 240 150"
      role="img"
      aria-label={label}
      className="h-auto w-full max-w-[240px]"
    >
      <rect x="4" y="8" width="232" height="136" rx="20" fill={A} opacity="0.07" />
      <ellipse cx="120" cy="132" rx="90" ry="8" fill={INK} opacity="0.06" />
      {children}
    </svg>
  );
}

const Doc = ({
  x,
  y,
  r = 0,
  lines = 4,
  stamp = false,
}: {
  x: number;
  y: number;
  r?: number;
  lines?: number;
  stamp?: boolean;
}) => (
  <g transform={`translate(${x} ${y}) rotate(${r})`}>
    <rect width="52" height="68" rx="6" fill="white" stroke={INK} strokeOpacity="0.15" />
    <rect x="8" y="9" width="24" height="5" rx="2.5" fill={A} opacity="0.8" />
    {Array.from({ length: lines }, (_, i) => (
      <rect
        key={i}
        x="8"
        y={22 + i * 9}
        width={i % 2 ? 28 : 36}
        height="4"
        rx="2"
        fill={INK}
        opacity="0.15"
      />
    ))}
    {stamp && <circle cx="40" cy="56" r="7" fill="none" stroke={A} strokeWidth="2" opacity="0.7" />}
  </g>
);

const Building = ({
  x,
  y,
  w = 46,
  h = 56,
  flag = false,
}: {
  x: number;
  y: number;
  w?: number;
  h?: number;
  flag?: boolean;
}) => (
  <g transform={`translate(${x} ${y})`}>
    <rect width={w} height={h} rx="4" fill="white" stroke={INK} strokeOpacity="0.15" />
    {Array.from({ length: Math.floor(h / 16) * 2 }, (_, i) => (
      <rect
        key={i}
        x={8 + (i % 2) * (w / 2 - 2)}
        y={8 + Math.floor(i / 2) * 15}
        width={w / 2 - 14}
        height="8"
        rx="1.5"
        fill={A}
        opacity="0.25"
      />
    ))}
    <rect x={w / 2 - 6} y={h - 14} width="12" height="14" rx="1.5" fill={A} opacity="0.7" />
    {flag && (
      <g>
        <line
          x1={w - 6}
          y1="0"
          x2={w - 6}
          y2="-16"
          stroke={INK}
          strokeOpacity="0.4"
          strokeWidth="1.5"
        />
        <path d={`M${w - 6} -16 h12 l-3 4 l3 4 h-12z`} fill={A} />
      </g>
    )}
  </g>
);

const Pin = ({ x, y, s = 1 }: { x: number; y: number; s?: number }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    <path d="M0 0 C-12 -14 -12 -30 0 -30 C12 -30 12 -14 0 0Z" fill={A} />
    <circle cx="0" cy="-19" r="4.5" fill="white" />
  </g>
);

const Person = ({ x, y, tone = 1 }: { x: number; y: number; tone?: number }) => (
  <g transform={`translate(${x} ${y})`}>
    <circle cx="0" cy="0" r="8" fill={A} opacity={0.35 + 0.3 * tone} />
    <path d="M-13 26 C-13 12 13 12 13 26Z" fill={A} opacity={0.25 + 0.3 * tone} />
  </g>
);

const Node = ({ x, y, w = 44, label }: { x: number; y: number; w?: number; label?: boolean }) => (
  <g transform={`translate(${x} ${y})`}>
    <rect
      x={-w / 2}
      y="-11"
      width={w}
      height="22"
      rx="7"
      fill="white"
      stroke={A}
      strokeOpacity="0.5"
    />
    {label !== false && (
      <rect x={-w / 2 + 8} y="-2.5" width={w - 16} height="5" rx="2.5" fill={INK} opacity="0.2" />
    )}
  </g>
);

const Line = ({ d }: { d: string }) => (
  <path d={d} fill="none" stroke={A} strokeOpacity="0.45" strokeWidth="1.5" />
);

const BASE_SCENES: Record<Exclude<WizardStepKey, 'processi' | 'obblighi' | 'diagnosi' | 'proposte'>, { label: string; art: ReactNode }> = {
  visura: {
    label: 'La visura camerale',
    art: (
      <>
        <Doc x={94} y={34} lines={5} stamp />
        <circle cx="150" cy="38" r="13" fill={A} />
        <path
          d="M144 38 l4 4 l8 -9"
          fill="none"
          stroke="white"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  impresa: {
    label: 'L’impresa con i suoi organi',
    art: (
      <>
        <Building x={92} y={50} w={56} h={70} flag />
        <Person x={50} y={70} />
        <Person x={190} y={70} tone={0.6} />
        <Line d="M62 80 Q78 70 92 78" />
        <Line d="M178 80 Q162 70 148 78" />
      </>
    ),
  },
  sedi: {
    label: 'Le sedi sulla mappa',
    art: (
      <>
        <path d="M30 110 L80 40 L130 100 L170 50 L215 110Z" fill={A} opacity="0.08" />
        <path
          d="M24 118 C70 96 110 128 150 104 S210 96 220 112"
          fill="none"
          stroke={INK}
          strokeOpacity="0.12"
          strokeWidth="6"
          strokeLinecap="round"
        />
        <Pin x={72} y={92} s={1.1} />
        <Pin x={148} y={78} />
        <Pin x={192} y={102} s={0.85} />
      </>
    ),
  },
  attivita: {
    label: 'Attività economiche in ogni sede',
    art: (
      <>
        <Building x={36} y={60} w={44} h={56} />
        <Building x={160} y={56} w={48} h={60} />
        <g transform="translate(120 52)">
          <circle r="22" fill="white" stroke={A} strokeOpacity="0.5" />
          <path d="M-9 -4 h18 M-9 4 h12" stroke={A} strokeWidth="3" strokeLinecap="round" />
        </g>
        <Line d="M80 84 Q100 70 100 60" />
        <Line d="M160 84 Q140 70 140 60" />
      </>
    ),
  },
  organizzazione: {
    label: 'Ruoli e organizzazione',
    art: (
      <>
        <Building x={98} y={30} w={44} h={40} flag />
        <Person x={60} y={92} />
        <Person x={92} y={98} tone={0.7} />
        <Person x={148} y={98} tone={0.5} />
        <Person x={180} y={92} tone={0.8} />
        <Line d="M120 70 L60 84 M120 70 L92 90 M120 70 L148 90 M120 70 L180 84" />
      </>
    ),
  },
  software: {
    label: 'I programmi e i servizi online dell’azienda',
    art: (
      <>
        <rect
          x="52"
          y="30"
          width="96"
          height="64"
          rx="8"
          fill="white"
          stroke={INK}
          strokeOpacity="0.15"
        />
        <rect x="52" y="30" width="96" height="12" rx="6" fill={A} opacity="0.15" />
        <circle cx="62" cy="36" r="2.5" fill={A} />
        <circle cx="70" cy="36" r="2.5" fill={A} opacity="0.5" />
        <rect x="62" y="52" width="22" height="16" rx="3" fill={A} opacity="0.7" />
        <rect x="90" y="52" width="22" height="16" rx="3" fill={A} opacity="0.4" />
        <rect x="118" y="52" width="22" height="16" rx="3" fill={A} opacity="0.25" />
        <rect x="62" y="76" width="78" height="5" rx="2.5" fill={INK} opacity="0.15" />
        <Line d="M100 94 v14 M84 108 h32" />
        <rect x="80" y="104" width="40" height="10" rx="3" fill={INK} opacity="0.12" />
      </>
    ),
  },
  impianti: {
    label: 'Impianti e attrezzature',
    art: (
      <>
        {/* Caldaia con manometro */}
        <rect
          x="52"
          y="38"
          width="56"
          height="78"
          rx="8"
          fill="white"
          stroke={INK}
          strokeOpacity="0.15"
        />
        <circle cx="80" cy="62" r="12" fill="none" stroke={A} strokeWidth="2.5" opacity="0.8" />
        <path d="M80 62 L87 55" stroke={A} strokeWidth="2.5" strokeLinecap="round" />
        <rect x="64" y="84" width="32" height="6" rx="3" fill={INK} opacity="0.15" />
        <rect x="64" y="96" width="22" height="6" rx="3" fill={INK} opacity="0.15" />
        <path
          d="M108 70 H128 V52"
          fill="none"
          stroke={INK}
          strokeOpacity="0.25"
          strokeWidth="5"
          strokeLinecap="round"
        />
        {/* Ingranaggio */}
        <g transform="translate(168 72)">
          {Array.from({ length: 8 }, (_, i) => (
            <rect
              key={i}
              x="-4"
              y="-27"
              width="8"
              height="12"
              rx="2"
              fill={A}
              opacity="0.75"
              transform={`rotate(${i * 45})`}
            />
          ))}
          <circle r="18" fill={A} opacity="0.75" />
          <circle r="7" fill="white" />
        </g>
        {/* Libretto */}
        <g transform="translate(150 104) rotate(-6)">
          <rect width="34" height="22" rx="3" fill="white" stroke={A} strokeOpacity="0.6" />
          <rect x="5" y="6" width="20" height="3" rx="1.5" fill={INK} opacity="0.2" />
          <rect x="5" y="12" width="14" height="3" rx="1.5" fill={INK} opacity="0.2" />
        </g>
      </>
    ),
  },
  procedure: {
    label: 'Le procedure passo per passo',
    art: (
      <>
        {[0, 1, 2, 3].map((i) => (
          <g key={i} transform={`translate(${46 + i * 48} ${i % 2 ? 84 : 62})`}>
            <circle r="14" fill={i < 2 ? A : 'white'} stroke={A} strokeOpacity="0.6" />
            {i < 2 && (
              <path
                d="M-6 0 l4 4 l8 -8"
                fill="none"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
          </g>
        ))}
        <Line d="M60 66 L80 80 M108 80 L128 66 M156 66 L176 80" />
      </>
    ),
  },
};

/** I passi delle fasi 4, 6, 10 e 11 (ADR 0174) riusano le scene più vicine. */
const SCENES: Record<WizardStepKey, { label: string; art: ReactNode }> = {
  ...BASE_SCENES,
  processi: { ...BASE_SCENES.attivita, label: 'I processi' },
  obblighi: { ...BASE_SCENES.procedure, label: 'Gli obblighi' },
  diagnosi: { ...BASE_SCENES.organizzazione, label: 'La diagnosi' },
  proposte: { ...BASE_SCENES.software, label: 'Proposte e percorso' },
};

export function StepIllustration({ step }: { step: WizardStepKey }) {
  const s = SCENES[step];
  return <Frame label={s.label}>{s.art}</Frame>;
}

/**
 * L'intestazione di un passo, uguale in tutti i passi e su ogni schermo: illustrazione a sinistra, a destra titolo e
 * descrizione (e ciò che il passo aggiunge sotto, come l'avanzamento delle conferme).
 */
export function StepIntro({
  step: _step,
  eyebrow,
  title,
  children,
}: {
  step: WizardStepKey;
  eyebrow?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
}) {
  // Come le sezioni di «La tua azienda» (ADR 0086): etichetta piccola, titolo, testo; niente illustrazione, il pannello
  // della fase dice già dove si è.
  return (
    <div className="px-1 text-left">
      {eyebrow && <p className="text-xs font-semibold uppercase tracking-wider text-ink/50">{eyebrow}</p>}
      <h2 className="mt-0.5 text-xl font-semibold leading-snug">{title}</h2>
      {children}
    </div>
  );
}
